import { API } from '../../api'
import { chatServiceUrl } from '../../utils/constants'
import { ChatDocumentDocType } from './api'

/** Response from the chat-document presign endpoint. */
export interface DocumentPresignResponse {
  upload_url: string
  s3_uri: string
  expires_in_sec: number
  content_type: string
}

/**
 * Presign a PUT for a funding document (SOW / pricing calculator PDF). The
 * returned `upload_url` is a short-lived S3 URL the client PUTs the file to,
 * and `s3_uri` is the reference handed back to the agent via the chat stream.
 */
export const presignChatDocumentAPI = (
  partnerId: string,
  body: {
    filename: string
    doc_type: ChatDocumentDocType
    content_type?: string
  }
) =>
  API.post<DocumentPresignResponse>(
    `${chatServiceUrl}/v2/partners/${partnerId}/documents/presign`,
    {
      content_type: body.content_type ?? 'application/pdf',
      ...body,
    }
  )

/**
 * Upload the file bytes straight to the presigned S3 URL with a bare PUT. This
 * deliberately bypasses the axios `API` client — the presigned URL is its own
 * authorization, and adding our Bearer header would break the S3 signature.
 */
export const uploadFileToPresignedUrl = async (
  uploadUrl: string,
  file: File,
  contentType?: string
): Promise<void> => {
  const type = (contentType || file.type || 'application/pdf').trim()
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: type ? { 'Content-Type': type } : {},
    body: file,
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    const suffix = detail ? `: ${detail.slice(0, 200)}` : ''
    throw new Error(`Document upload failed (${response.status})${suffix}`)
  }
}
